import { KeyValue } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatRadioChange } from "@angular/material/radio";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { BaseComponent } from "../base-component";
import {
  AFAddressHistoryDomain,
  KeyVal,
  OusCountryAttachmentType,
  OusData,
  AF_CONSTANTS,
  Attachment,
} from "../model/af.model";
import { AddressService } from "../service/address.service";
import { AuthService } from "../service/auth.service";
import { EventService } from "../service/event.service";
import {
  ChangeNavigationEvent,
  ProgressBarEvent,
  ValidateAndNavigateEvent,
  ConsumerPrivacyActEvent,
  SkipToMainContentEvent,
  AttachmentUploadedEvent,
} from "../service/events";
import { CustomValidatorsService } from "../service/custom-validators.service";
import { MatSelectionListChange } from "@angular/material/list";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ErrorDialogComponent } from "../error-dialog/error-dialog.component";
import { MatTableDataSource } from "@angular/material/table";
import { FileDialogComponent } from "../file-dialog/file-dialog.component";

@Component({
  selector: "app-address-history",
  templateUrl: "./address-history.component.html",
  styleUrls: ["./address-history.component.scss"],
  standalone: false
})
export class AddressHistoryComponent
  extends BaseComponent
  implements OnInit, AfterViewInit, OnDestroy {
  maxDate = new Date();
  minDate = new Date(1900, 0, 1);
  addressData: AFAddressHistoryDomain;
  addressGroup: UntypedFormGroup;
  step: number = 0;
  showOusQuestion: boolean = false;
  ousCase: boolean = false;
  addressInstruction: string;
  sticky: boolean = false;
  menuPosition: any;
  cpaFlag: boolean = false;
  listCheck: boolean = false;
  ousAttachmentList: Array<OusCountryAttachmentType>;
  requiredOusAttachmentList: Array<OusCountryAttachmentType>;
  ousQuestionEligible: string;
  militaryQuestions: KeyVal[] = [
    { id: "Yes", value: "Yes" },
    { id: "No", value: "No" },
  ];
  ousMilitaryCheck: boolean = false;
  addressChange: boolean = false;
  check: boolean = false;
  border: boolean = false;
  containerWidth: number;
  jobCountry: string;
  jobSubCountry: string = "";
  jobCountryAbbreviation: string;
  addressLimit: number;
  addressHistoryOusInstruction: number;
  outsideJobCountryDocument: number;
  ukBasicDisclosureFlag:boolean=false;
  ousCountry: boolean = false;

  dataSource = new MatTableDataSource<Attachment>();
  displayedColumns: string[] = [
    "fileName",
    "descOfFileName",
    "fileType",
    "fileSize",
    "attachedBy",
    "attachedDateString",
    "remove",
  ];

  safeStrRegex: RegExp = /^[a-zA-Z0-9\s\-&().,'"\\/:;]*$/;
  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]*$/;

  @ViewChild("stickyDiv") stickyDivElement: ElementRef;
  @ViewChildren("stickyDiv", { read: ViewContainerRef })
  viewContainerRefs: QueryList<any>;

  @ViewChild("addElement") addElement: ElementRef;
  @ViewChild("addButtonElement") addButtonElement: ElementRef;

  constructor(
    private readonly userService: AuthService,
    private readonly addressService: AddressService,
    private readonly fb: UntypedFormBuilder,
    private readonly eventBus: EventService,
    private readonly _router: Router,
    private readonly _snackBar: MatSnackBar,
    private readonly customValidator: CustomValidatorsService,
    private readonly dialog: MatDialog,
    public dialogRef: MatDialogRef<ErrorDialogComponent>
  ) {
    super(eventBus, _router);
    dialogRef.disableClose = true;
    this.ukBasicDisclosureFlag = this.userService.isSwapsAP();
    this.jobSubCountry = this.userService.getJobSubCountry();
  }

  ngOnInit() {
    this.createGroup();
    this.subscribeEvents();

  }

  subscribeEvents() {
    this.addressService.getAddressHistory().subscribe({
      next: (data: AFAddressHistoryDomain) => {
        this.addressData = data;
        this.ousAttachmentList = data.ousAttachmentList;
        this.requiredOusAttachmentList = data.requiredOusAttachmentList;
        this.showOusQuestion =
          this.addressData.ousQuestionEligible === "1" &&
          this.addressData.lockBoxFlag !== "0";
        this.ousCase =
          null !== this.addressData.jobCountryId &&
          this.addressData.jobCountryId !== 186;
        this.jobCountry = this.userService.getJobCountryValue();
        this.jobCountryAbbreviation = this.userService.getJobCountry();
        this.populateAddressHistory(this.addressData);
        this.checkCpaCountry();
        this.addressLimit = this.userService.getAddressLimit();
        this.addressHistoryOusInstruction =
          this.userService.getAddressHistoryInstruction();
        this.outsideJobCountryDocument =
          this.userService.getOutsideJobCountryDocument();
        this.dataSource.data = data.attachmentList;
        this.addInstruction(this.addressData);
        this.dataSource._updateChangeSubscription();
        this.setUKDisclosure();
      },
      error: (error: Error) => {
        console.log("Error: ", error);
      }
    });

    this.sub = this.eventBus.on(ValidateAndNavigateEvent, (event): void => {
      if (event.payload.currentLink === "/addressHistory") {
        this.submit.setValue(false);
        this.bookmarkUrl.setValue("");
        if (event.payload.save) {
          this.submitAddressHistory(this.addressGroup.value, event);
        } else if (event.payload.saveAndFinishLater) {
          this.bookmarkUrl.setValue(event.payload.currentLink);
          this.submitAddressHistory(this.addressGroup.value, event);
        } else if (this.validate()) {
          this.submitAddressHistory(this.addressGroup.value, event);
        }
      }
    });

    this.eventBus.on(SkipToMainContentEvent, (event): void => {
      if (event.payload.text && event.payload.text === "/addressHistory") {
        let mainContentElement = document.querySelector("#main-content"); //check if main-content id exists on page
        if (mainContentElement) {
          mainContentElement.scrollIntoView(true);
        }
      }
    });

    this.eventBus.on(AttachmentUploadedEvent, (event): void => {
      if (event.payload.file) {
        let attachment = event.payload.file as Attachment;
        this.dataSource.data.push(attachment);
        this.dataSource._updateChangeSubscription();

        if (event.payload.file.typeOfAttachmentId) {
          this.addressList
            .at(this.step)
            .get("mongoAttachmentId")
            .setValue(attachment.mongoAttachmentId);
          setTimeout(() => {
            let progress = this.getPercentage(this.addressGroup);
            this.eventBus.emit(new ProgressBarEvent({ id: progress }));
          }, 100);
        }
      }
    });
  }

   setUKDisclosure() {
      this.ukDisclosure.setValue(this.addressData.ukDisclosure || "");
      if (this.ukBasicDisclosureFlag) {
        this.addValidators(this.ukDisclosure);
      } else {
        this.clearValidators(this.ukDisclosure);
      }
    }

    clearValidators(control: AbstractControl) {
      control?.reset();
      control?.clearValidators();
      control?.setErrors(null);
      control?.updateValueAndValidity();
    }
  
    addValidators(control: AbstractControl) {
      control?.setValidators([Validators.required]);
      control?.updateValueAndValidity();
    }

  onFocusOutMainContent() {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  addInstruction(addressData: AFAddressHistoryDomain) {
    if (this.addressData.jobCountryId == 78) {
      this.addressInstruction =
        "Please enter your current as well as permanent address. If you are residing at your current address for less than 6 months then please enter your current, previous and permanent address.";
      return;
    }

    if (addressData.addressLimit == 0) {
      if (addressData.haveYouLivedOUS == 1) {
        this.addressInstruction =
          "Please enter current U.S. address and any applicable address outside the U.S. within the past 5 years.";
      } else {
        this.addressInstruction = "Please enter current address.";
      }
    } else {
      let years = "years";
      if (addressData.addressLimit == 1) {
        years = "year";
      }
      this.addressInstruction =
        'Please enter "' +
        this.addressData.addressLimit +
        " " +
        years +
        '"  of address history, in chronological order, starting with your  most recent address.';
    }
    if (this.addressHistoryOusInstruction == 1) {
      this.addressInstruction =
        this.addressInstruction +
        "This includes all international, permanent and temporary addresses.";
    }
  }

  submitAddressHistory(addressHistory, event) {
    this.addressService.submitAddressHistory(addressHistory).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(["sessionTimedOut"]);
        } else {
          this.eventBus.emit(
            new ChangeNavigationEvent({ id: event.payload.id })
          );
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  setStep(index: number) {
    this.step = index;
  }

  get addressList() {
    return this.addressGroup.get("addressList") as UntypedFormArray;
  }
  get haveYouLivedOUS() {
    return this.addressGroup.get("haveYouLivedOUS");
  }
  get verifyId() {
    return this.addressGroup.get("verifyId");
  }
  get submit() {
    return this.addressGroup.get("submit");
  }
  get bookmarkUrl() {
    return this.addressGroup.get("bookmarkUrl");
  }
  get haveYouLivedOutsideJobCountry() {
    return this.addressGroup.get("haveYouLivedOutsideJobCountry");
  }
  get ukDisclosure(){
    return this.addressGroup.get("ukDisclosure");
  }

  populateAddressHistory(addressData: AFAddressHistoryDomain) {
    this.addressGroup.patchValue(addressData);
    for (let address of this.addressData.addressList) {
      let fg: UntypedFormGroup = this.fieldsGroup();
      fg.patchValue(address);
      this.addressList.push(fg);
      this.setValidators(fg);
      this.setOUSValidators(fg);
    }
    if (this.addressList.length == 0) {
      this.createComponent();
    }
  }

  onChangeCountry(countryId: any, control: UntypedFormGroup) {
    const countryList = new Array();
    for (let container of this.addressGroup.get("addressList")["controls"]) {
      for (let controlKey in container.controls) {
        if (controlKey == "country") {
          countryList.push(container.controls[controlKey].value);
        }
      }
    }

    this.addressService.getOusData(countryList, countryId).subscribe({
      next: (data: OusData) => {
        control.get("showMotherName").setValue(data.showMotherName);
        control.get("showFatherName").setValue(data.showFatherName);
        control.get("showBirthPlace").setValue(data.showBirthPlace);
        control.get("showMotherMaidenName").setValue(data.showMotherMaidenName);
        this.setOUSValidators(control);
        this.ousAttachmentList = data.ousAttachmentList;
      },
      error: (error: any) => console.log(error)
    }
    );
  }

  fieldsGroup(): UntypedFormGroup {
    let grp: UntypedFormGroup = this.fb.group(
      {
        isCurrentAddress: ["", [Validators.required]],
        startDate: ["", [Validators.required, Validators.maxLength(100)]],
        endDate: ["", [Validators.required, Validators.maxLength(100)]],
        streetAddress1: [
          "",
          [
            Validators.required,
            Validators.maxLength(600),
            this.customValidator.validateRequiredField,
            Validators.pattern(this.safeStrRegex)
          ],
        ],
        streetAddress2: ["", [Validators.maxLength(255), Validators.pattern(this.safeStrRegex)]],
        streetAddress3: ["", [Validators.maxLength(255), Validators.pattern(this.safeStrRegex)]],
        zipCode: [
          "",
          [
            Validators.required,
            Validators.maxLength(13),
            this.customValidator.validateRequiredField,
            Validators.pattern(this.safeStrRegex)
          ],
        ],
        city: [
          "",
          [
            Validators.required,
            Validators.maxLength(200),
            this.customValidator.validateRequiredField,
            Validators.pattern(this.safeStrRegex)
          ],
        ],
        country: ["", [Validators.required]],
        state: ["", [Validators.maxLength(100), Validators.pattern(this.safeStrRegex)]],
        county: [
          "",
          [
            Validators.required,
            Validators.maxLength(100),
            this.customValidator.validateRequiredField,
            Validators.pattern(this.safeStrRegex)
          ],
        ],
        californiaCounty: [""],
        isLACounty: [""],
        usState: [""],
        // ous fields
        showMotherName: [false],
        showFatherName: [false],
        showBirthPlace: [false],
        showMotherMaidenName: [false],
        militaryBase: [""],
        motherFirstName: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        motherMiddleName: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        motherLastName: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        motherMaidenName: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        fatherFirstName: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        fatherMiddleName: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        fatherLastName: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        countryOfBirth: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        townOfBirth: ["", [Validators.maxLength(100), Validators.pattern(this.regexName)]],
        checkTwo: [false],
        checkTwoCounty: [false],
        attachementTypeId: [""],
        mongoAttachmentId: [""],
      },
      { validators: datesValidator() }
    );

    if (this.addressData.jobCountryId == 78) {
      grp.get("attachementTypeId").setValidators(Validators.required);
      grp.get("mongoAttachmentId").setValidators(Validators.required);
      grp.get("county").setValidators(null);
      grp.get("county").setErrors(null);
      grp.get("county").updateValueAndValidity();
    } else {
      grp.get("attachementTypeId").setValidators(null);
      grp.get("mongoAttachmentId").setValidators(null);
    }



    return grp;
  }

  onAddAddress() {
    setTimeout(() => {
      this.addElement.nativeElement.focus();
      this.border = true;
    });
    this.createComponent();
  }

  createComponent() {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.addressList.push(fg);
    this.step = this.addressList.length - 1;
    this.submit.setValue(false);
    this.setValidators(fg);
    this.setOUSValidators(fg);
  }

  downloadAttachment(attachmentId: string, fileName: string): void {
    this.userService.downloadAttachment(attachmentId, fileName).subscribe();
  }    

  validate() {
    this.submit.setValue(true);

    if (this.addressList && this.addressList.length > 0) {
      this.addressList.markAllAsTouched();
    }

    this.addressGroup.markAllAsTouched();
    if (this.addressGroup?.invalid) {
      let isInvalid = true;
      const firstInvalidField = Object.keys(this.addressGroup.controls).find(field => this.addressGroup.get(field)?.invalid);
      if (this.addressGroup.controls[firstInvalidField] instanceof FormArray) {
        (this.addressGroup.controls[firstInvalidField]).controls.forEach((data: FormGroup) => {

          // Refresh on unsaved address with attachment fails validation as document is retained in mongo.
          if (Object.keys(data.controls).find(field => data.get(field)?.invalid) === "mongoAttachmentId") {
            this._snackBar.open("Error: Please re-upload required documents, or provide missing document(s) for the required address.", "Close", {
              duration: 6000,
            });
            isInvalid = false;
          }

          if (data.invalid && isInvalid) {
            this.userService.setFocus(data);
            isInvalid = false;
          }
        })
      } else {
        this.userService.setFocus(this.addressGroup);
      }
    }

    return !this.addressGroup.invalid;
  }

  validateOusCountry() {
    let valid = false;
    if (this.haveYouLivedOUS.value === 1) {
      valid = this.validateOusCountryAddr(valid);
    } else {
      valid = true;
    }
    return valid;
  }

  validateOusCountryAddr(valid: boolean) {
    for (let container of this.addressGroup.get("addressList")["controls"]) {
      for (let controlKey in container.controls) {
        if (controlKey == "country") {
          if (container.controls[controlKey].value !== "186") valid = true;
        }
      }
    }
    return valid;
  }

  getPercentage(addressGroup: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;
    let container: UntypedFormGroup = addressGroup;

    for (let controlKey in container.controls) {
      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(
            container.controls[controlKey].errors
          ).length;
        }
      }
    }

    for (let container of addressGroup.get("addressList")["controls"]) {
      let channelArray: Array<string> = [];

      if (container.controls["isCurrentAddress"].value === "1" && container.controls["usState"].value === "5") {
        channelArray = channelArray.concat(["county"]);
      } else if (this.addressData.jobCountryId === 78) {
        channelArray = channelArray.concat(["californiaCounty", "isLACounty", "county"]);
      } else {
        channelArray = channelArray.concat(["californiaCounty", "isLACounty"]);
      }

      for (let controlKey in container.controls) {
        if (channelArray.some(x => x === controlKey)) {
          continue;
        }

        if (container.controls.hasOwnProperty(controlKey)) {
          if (container.controls[controlKey].validator) {
            totalCount += container.controls[controlKey].validator.length;
          }
          if (container.controls[controlKey].errors) {
            errorCount += Object.keys(
              container.controls[controlKey].errors
            ).length;
          }
        }
      }
    }

    return ((totalCount - errorCount) / totalCount) * 100;
  }

  scroll(el: HTMLElement) {
    el.scrollIntoView({ behavior: "smooth", block: "end", inline: "end" });
  }

  ngAfterViewInit() {
    this.viewContainerRefs.changes.subscribe(() => {
      if (this.viewContainerRefs.toArray().length) {
        if (this.stickyDivElement) {
          this.containerWidth = this.stickyDivElement.nativeElement.clientWidth;
        }
      }
    });

    this.addressGroup.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.addressGroup);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      });

      this.ousMilitaryCheck = false;

      for (let container of this.addressGroup.get("addressList")["controls"]) {
        let checkMilitaryBase = container.controls["militaryBase"].value;

        if (checkMilitaryBase && checkMilitaryBase === "No") {
          this.ousMilitaryCheck = true;
          break;
        } else {
          this.ousMilitaryCheck = false;
        }
      }

      if (this.addressList.length !== 0) {
        this.listCheck = true;
      } else {
        this.listCheck = false;
      }
    });

    // retain navigation on page refresh
    let navlink = this.userService
      .getNavLinks()
      .find((e) => e.routerLink === "/addressHistory");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));

    this.handleZoom();
  }

  checkCpaCountry() {
    let cpaStates: string[] = [];

    for (let container of this.addressGroup.get("addressList")["controls"]) {
      let usState = container.controls["usState"].value;
      let isCurrentAddress = container.controls["isCurrentAddress"].value;
      this.cpaFlag =
        this.addressData.activeCpaStates.indexOf(usState) !== -1 &&
        isCurrentAddress === "1";

      if (this.cpaFlag) {
        cpaStates.push(usState);
      }
    }

    if (
      this.addressData.jobStateId &&
      cpaStates.indexOf(this.addressData.jobStateId) !== -1
    ) {
      cpaStates.push(this.addressData.jobStateId);
    }

    this.cpaFlag = cpaStates.length > 0;
    this.eventBus.emit(
      new ConsumerPrivacyActEvent({ show: this.cpaFlag, cpaStates: cpaStates })
    );
  }

  handleSelection(event: MatSelectionListChange) {
    event.source.deselectAll();
    event.options[0].selected = true;
    this.step = event.options[0].value;
  }

  onHaveYouLivedOUSChange(mrChange: MatRadioChange) {
    if (mrChange.value === 1) {
      this.addressInstruction =
        "Please enter current U.S. address and any applicable address outside the U.S. within the past 5 years.";
    } else {
      this.addressInstruction = "Please enter current address.";
    }

    for (let container of this.addressGroup.get("addressList")["controls"]) {
      this.setOUSValidators(container);
    }

    setTimeout(() => {
      let progress = this.getPercentage(this.addressGroup);
      this.eventBus.emit(new ProgressBarEvent({ id: progress }));
    });
  }

  onHaveYouLivedOutsideJobCountryChange(mrChange: MatRadioChange) {
    if (this.outsideJobCountryDocument === 1 && mrChange.value === 1) {
      const dialogMsg =
        "Please see Required Documents section. Identification information will be requested for each additional country entered.";

      this.dialogRef = this.dialog.open(ErrorDialogComponent, {
        ariaLabel: "Required Documents",
        role: "alertdialog",
        autoFocus: false,
        disableClose: true,
        width: "550px",
        data: {
          dialogData: dialogMsg,
        },
      });

      this.dialogRef.afterClosed().subscribe(() => {
        window.scrollTo(0, 0);
      });
    }
  }

  deleteClick(i: number) {
    const countryWorkVar = this.addressList.at(i).value.country;
    if (this.addressList.at(i).value.mongoAttachmentId) {
      this.removeAttachment(this.addressList.at(i).value.mongoAttachmentId);
    }

    this.addressList.removeAt(i);
    if (i === this.addressList.length) {
      this.step = i - 1;
    }

    setTimeout(() => {
      if (this.addressList && this.addressList.length === 0) {
        this.addButtonElement.nativeElement.focus();
        this.border = false;
      } else {
        this.addElement.nativeElement.focus();
        this.border = true;
      }
    }, 100);

    if (countryWorkVar && "186" !== countryWorkVar) {
      const countryList = this.getCountryList();

      this.addressService.getOusData(countryList, countryWorkVar).subscribe({
        next: (data: OusData) => {
          this.ousAttachmentList = data.ousAttachmentList;
        },
        error: (error: any) => console.log(error)
      });
    }
  }

  getCountryList() {
    const countryList = new Array();
    for (let container of this.addressGroup.get("addressList")["controls"]) {
      for (let controlKey in container.controls) {
        if (controlKey === "country") {
          countryList.push(container.controls[controlKey].value);
        }
      }
    }
    return countryList;
  }

  focusTarget() {
    setTimeout(() => {
      this.addElement.nativeElement.focus();
      this.border = true;
    }, 100);
  }

  @HostListener("document:keydown", ["$event"])
  clearBorder(keyDown: KeyboardEvent) {
    if (keyDown.key === "Tab") {
      this.border = false;
    }
  }

  @HostListener("click")
  clearBorderClick() {
    this.border = false;
  }

  valueAscOrder = (
    a: KeyValue<number, string>,
    b: KeyValue<number, string>
  ): number => {
    return a.value.localeCompare(b.value);
  };

  createGroup() {
    this.addressGroup = this.fb.group({
      haveYouLivedOUS: ["", [Validators.required]],
      haveYouLivedOutsideJobCountry: ["", [Validators.required]],
      ukDisclosure:["", [Validators.required]],
      addressList: new UntypedFormArray([]),
      verifyId: [this.userService.getVerifyId(), []],
      militaryBase: [""],
      addressLimit: [],
      jobCountryId: [],
      jobCountryValue: [this.userService.getJobCountryValue(), []],
      eligibility: [""],
      visaSponsorShipRequired: [""],
      authorised: [""],
      submit: [false],
      bookmarkUrl: [""],
    });
  }

  @HostListener("window:scroll")
  handleScroll() {
    const windowScroll = window.scrollY;

    if (
      windowScroll >= AF_CONSTANTS.windowScroll_Lower_limit &&
      windowScroll <= 845
    ) {
      this.sticky = true;
    } else {
      this.sticky = false;
    }
  }

  @HostListener("window:resize")
  @HostListener("window:load")
  handleZoom() {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    if (windowWidth <= AF_CONSTANTS.windowWidth || windowHeight <= 580) {
      this.addressChange = true;
    } else {
      this.addressChange = false;
    }
  }

  ousOnBlur() {
    if (this.haveYouLivedOUS.value) {
      this.check = false;
    } else if (this.haveYouLivedOUS.value === 0) {
      this.check = false;
    } else {
      this.check = true;
    }
  }

  ousOnFocus() {
    this.check = false;
  }

  currentOnBlur(i: number) {
    if (this.addressList.at(i).get("isCurrentAddress").value) {
      this.addressList.at(i).get("checkTwo").setValue(false);
    } else {
      this.addressList.at(i).get("checkTwo").setValue(true);
    }
  }

  currentOnFocus(i: number) {
    this.addressList.at(i).get("checkTwo").setValue(false);
  }

  countyOnBlur(i: number) {
    if (this.addressList.at(i).get("isCurrentAddress").value) {
      this.addressList.at(i).get("checkTwoCounty").setValue(false);
    } else {
      this.addressList.at(i).get("checkTwoCounty").setValue(true);
    }
  }

  countyOnFocus(i: number) {
    this.addressList.at(i).get("checkTwoCounty").setValue(false);
  }

  dropDownScreenReaderCountry(address: any): string {
    if (address.controls.country.value) {
      return (
        "Selected " +
        this.addressData.countryLookup[
        Object.keys(this.addressData.countryLookup).find(
          (e) => e === address.controls.country.value
        )
        ]
      );
    } else {
      return "blank";
    }
  }

  dropDownScreenReaderState(address: any): string {
    if (address.controls.usState.value) {
      return (
        "Selected " +
        this.addressData.usStateLookup[
        Object.keys(this.addressData.usStateLookup).find(
          (e) => e === address.controls.usState.value
        )
        ]
      );
    } else {
      return "blank";
    }
  }

  dropDownScreenReaderMilitary(address: any): string {
    if (address.controls.militaryBase.value) {
      return "Selected " + address.controls.militaryBase.value;
    } else {
      return "blank";
    }
  }

  setOUSValidators(control: UntypedFormGroup) {
    const showMotherName = control.get("showMotherName");
    const showFatherName = control.get("showFatherName");
    const showBirthPlace = control.get("showBirthPlace");
    const showMotherMaidenName = control.get("showMotherMaidenName");

    const motherMaidenName = control.get("motherMaidenName");
    const motherFirstName = control.get("motherFirstName");
    const motherLastName = control.get("motherLastName");
    const fatherFirstName = control.get("fatherFirstName");
    const fatherLastName = control.get("fatherLastName");
    const countryOfBirth = control.get("countryOfBirth");
    const townOfBirth = control.get("townOfBirth");
    const militaryBase = control.get("militaryBase");
    const country = control.get("country");

    if (this.haveYouLivedOUS.value !== 1) {
      //disable all validators
      motherMaidenName.setValidators(null);
      motherMaidenName.setErrors(null);
      motherFirstName.setValidators(null);
      motherFirstName.setErrors(null);
      motherLastName.setValidators(null);
      motherLastName.setErrors(null);
      fatherFirstName.setValidators(null);
      fatherFirstName.setErrors(null);
      fatherLastName.setValidators(null);
      fatherLastName.setErrors(null);
      countryOfBirth.setValidators(null);
      countryOfBirth.setErrors(null);
      townOfBirth.setValidators(null);
      townOfBirth.setErrors(null);
      militaryBase.setValidators(null);
      militaryBase.setErrors(null);
    } else {
      // conditional validations
      if (country.value && country.value !== "186") {
        militaryBase.setValidators([Validators.required]);
      } else {
        militaryBase.setValidators(null);
        militaryBase.setErrors(null);
      }

      if (country.value && country.value !== "186" && showMotherName.value) {
        motherFirstName.setValidators([
          Validators.required,
          this.customValidator.validateRequiredField,
          Validators.maxLength(100),
          Validators.pattern(this.regexName)
        ]);
        motherLastName.setValidators([
          Validators.required,
          this.customValidator.validateRequiredField,
          Validators.maxLength(100),
          Validators.pattern(this.regexName)
        ]);
      } else {
        motherFirstName.setValidators(null);
        motherFirstName.setErrors(null);
        motherLastName.setValidators(null);
        motherLastName.setErrors(null);
      }

      if (country.value && country.value !== "186" && showFatherName.value) {
        fatherFirstName.setValidators([
          Validators.required,
          this.customValidator.validateRequiredField,
          Validators.maxLength(100),
          Validators.pattern(this.regexName)
        ]);
        fatherLastName.setValidators([
          Validators.required,
          this.customValidator.validateRequiredField,
          Validators.maxLength(100),
          Validators.pattern(this.regexName)
        ]);
      } else {
        fatherFirstName.setValidators(null);
        fatherFirstName.setErrors(null);
        fatherLastName.setValidators(null);
        fatherLastName.setErrors(null);
      }

      if (
        country.value &&
        country.value !== "186" &&
        showMotherMaidenName.value
      ) {
        motherMaidenName.setValidators([
          Validators.required,
          this.customValidator.validateRequiredField,
          Validators.maxLength(100),
          Validators.pattern(this.regexName)
        ]);
      } else {
        motherMaidenName.setValidators(null);
        motherMaidenName.setErrors(null);
      }

      if (country.value && country.value !== "186" && showBirthPlace.value) {
        countryOfBirth.setValidators([
          Validators.required,
          this.customValidator.validateRequiredField,
          Validators.maxLength(100),
          Validators.pattern(this.regexName)
        ]);
        townOfBirth.setValidators([
          Validators.required,
          this.customValidator.validateRequiredField,
          Validators.maxLength(100),
          Validators.pattern(this.regexName)
        ]);
      } else {
        countryOfBirth.setValidators(null);
        countryOfBirth.setErrors(null);
        townOfBirth.setValidators(null);
        townOfBirth.setErrors(null);
      }
    }

    militaryBase.updateValueAndValidity();
    motherMaidenName.updateValueAndValidity();
    motherFirstName.updateValueAndValidity();
    motherLastName.updateValueAndValidity();
    fatherFirstName.updateValueAndValidity();
    fatherLastName.updateValueAndValidity();
    countryOfBirth.updateValueAndValidity();
    townOfBirth.updateValueAndValidity();
  }

  setValidators(control: UntypedFormGroup) {
    const isCurrentAddress = control.get("isCurrentAddress");
    const endDate = control.get("endDate");
    const country = control.get("country");
    const usState = control.get("usState");
    const state = control.get("state");
    const californiaCounty = control.get("californiaCounty");
    const isLACounty = control.get("isLACounty");
    const county = control.get("county");

    if (country.value === "186") {
      usState.setValidators([Validators.required]);
    } else {
      usState.setValidators(null);
      usState.setErrors(null);
    }

    if (isCurrentAddress.value === "1" && usState.value === "5") {
      californiaCounty.markAsUntouched();
      californiaCounty.setValidators([Validators.required]);
      californiaCounty.updateValueAndValidity();

      county.setValidators(null);
      county.setErrors(null);
      county.reset();
      county.markAsPristine();
      county.updateValueAndValidity();

      if (californiaCounty.value === "19") {
        isLACounty.markAsUntouched();
        isLACounty.setValidators([Validators.required]);
        isLACounty.updateValueAndValidity();
      } else {
        isLACounty.setValidators(null);
        isLACounty.setErrors(null);
        isLACounty.reset();
        isLACounty.markAsPristine();
        isLACounty.updateValueAndValidity();
      }
    } else {
      californiaCounty.setValidators(null);
      californiaCounty.setErrors(null);
      californiaCounty.reset();
      californiaCounty.markAsPristine();
      californiaCounty.updateValueAndValidity();

      if (this.addressData.jobCountryId !== 78) {
        county.markAsUntouched();
        county.setValidators([Validators.required,
        Validators.maxLength(100),
        this.customValidator.validateRequiredField,
        Validators.pattern(this.safeStrRegex)]);
        county.updateValueAndValidity();
      }
    }

    if (!this.showOusQuestion || this.ousCase) {
      this.haveYouLivedOUS.setValidators(null);
      this.haveYouLivedOUS.setErrors(null);
    }

    if (!this.ousCase || this.addressData.jobCountryId == 78) {
      this.haveYouLivedOutsideJobCountry.setValidators(null);
      this.haveYouLivedOutsideJobCountry.setErrors(null);
    }

    country.valueChanges.subscribe((country) => {
      if (country === "186") {
        usState.setValidators([Validators.required]);
      } else {
        usState.setValidators(null);
        usState.setErrors(null);
      }

      usState.updateValueAndValidity();
      state.updateValueAndValidity();

      if (
        this.showOusQuestion &&
        !this.ousCase &&
        this.haveYouLivedOUS.value == 1 &&
        country
      ) {
        if (country !== "186") {
          this.onChangeCountry(country, control);
        } else {
          this.setOUSValidators(control);
        }
      }
    });

    usState.valueChanges.subscribe((usState) => {
      if (isCurrentAddress.value === "1" && usState === "5") {
        californiaCounty.markAsUntouched();
        californiaCounty.setValidators([Validators.required]);
        californiaCounty.updateValueAndValidity();

        county.setValidators(null);
        county.setErrors(null);
        county.reset();
        county.markAsPristine();
        county.updateValueAndValidity();

      } else {
        californiaCounty.setValidators(null);
        californiaCounty.setErrors(null);
        californiaCounty.reset();
        californiaCounty.markAsPristine();
        californiaCounty.updateValueAndValidity();

        if (this.addressData.jobCountryId !== 78) {
          county.markAsUntouched();
          county.setValidators([Validators.required,
          Validators.maxLength(100),
          this.customValidator.validateRequiredField,
          Validators.pattern(this.safeStrRegex)]);
          county.updateValueAndValidity();
        }
      }
    });

    californiaCounty.valueChanges.subscribe((county) => {
      if (county === "19") {
        isLACounty.markAsUntouched();
        isLACounty.setValidators([Validators.required]);
        isLACounty.updateValueAndValidity();
      } else {
        isLACounty.setValidators(null);
        isLACounty.setErrors(null);
        isLACounty.reset();
        isLACounty.markAsPristine();
        isLACounty.updateValueAndValidity();
      }
    });

    isCurrentAddress.valueChanges.subscribe((isCurrentAddress) => {
      if (isCurrentAddress === "0" || isCurrentAddress === 0) {
        endDate.setValidators([Validators.required]);
      } else {
        endDate.setValidators(null);
        endDate.setErrors(null);
      }
      endDate.updateValueAndValidity();
      this.checkCpaCountry();

      if ((isCurrentAddress === "1" || isCurrentAddress === 1) && usState.value === "5") {
        californiaCounty.markAsPristine();
        californiaCounty.setValidators([Validators.required]);
        californiaCounty.updateValueAndValidity();

        county.setValidators(null);
        county.setErrors(null);
        county.reset();
        county.markAsPristine();
        county.updateValueAndValidity();
      } else if ((isCurrentAddress === "0" || isCurrentAddress === 0) && usState.value === "5") {
        californiaCounty.setValidators(null);
        californiaCounty.setErrors(null);
        californiaCounty.reset();
        californiaCounty.markAsPristine();
        californiaCounty.updateValueAndValidity();

        isLACounty.setValidators(null);
        isLACounty.setErrors(null);
        isLACounty.reset();
        isLACounty.markAsPristine();
        isLACounty.updateValueAndValidity();

        if (this.addressData.jobCountryId !== 78) {
          county.markAsPristine();
          county.setValidators([Validators.required,
          Validators.maxLength(100),
          this.customValidator.validateRequiredField,
          Validators.pattern(this.safeStrRegex)]);
          county.updateValueAndValidity();
        }
      }
    });

    usState.valueChanges.subscribe(() => {
      this.checkCpaCountry();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  openFileDialog(ousAttachments, ousAttachmentId) {
    const dialogRef = this.dialog.open(FileDialogComponent, {
      width: "550px",
      role: "alertdialog",
      data: {
        fileDescriptionLookup: "",
        ousAttachments: ousAttachments,
        ousAttachmentId: ousAttachmentId,
        ousAttachmentDesc: "addresshistory",
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      if (ousAttachments) {
        this.dataSource._updateChangeSubscription();
        setTimeout(() => {
          this.addElement.nativeElement.focus();
        }, 100);
      }
    });
  }

  removeAttachment(attachmentId) {
    this.addressService.removeAttachment(attachmentId).subscribe({
      next: () => {
        this._snackBar.open("Attachment removed successfully", "Close", {
          duration: 4000,
        });

        this.dataSource.data = this.dataSource.data.filter((value) => {
          if (value.mongoAttachmentId) {
            return value.mongoAttachmentId != attachmentId;
          } else {
            return value.attachmentId != attachmentId;
          }
        });

        this.dataSource._updateChangeSubscription();

        for (let address of this.addressList.controls) {
          if (address.get("mongoAttachmentId").value == attachmentId) {
            this.addressService
              .removeAddressHisAttachment(attachmentId)
              .subscribe(() => {
                address.get("mongoAttachmentId").setValue("");
                address.get("attachementTypeId").setValue("");
              });
            break;
          }
        }

        setTimeout(() => {
          let progress = this.getPercentage(this.addressGroup);
          this.eventBus.emit(new ProgressBarEvent({ id: progress }));
        }, 100);
      },
      error: (error: any) => console.log(error)
    });
  }

  downloadLADocument(): void {
    let fileName = "LA County Unincorp";
    this.userService.downloadLADocument(fileName).subscribe();
  }
}

export function datesValidator(): ValidatorFn {
  return (control: UntypedFormGroup): ValidationErrors | null => {
    const errors: ValidationErrors = {};
    const isCurrentAddress = control.get("isCurrentAddress");
    const endDate = control.get("endDate");
    const startDate = control.get("startDate");

    if (isCurrentAddress.value == "1") {
      endDate.setErrors(null);
      return Object.keys(errors).length ? errors : null;
    }

    if (
      startDate.value &&
      endDate.value &&
      !startDate.errors &&
      !endDate.errors
    ) {
      if (startDate.value > endDate.value) {
        endDate.setErrors({ noMatch: true });
      } else {
        endDate.setErrors(null);
      }
    }

    return Object.keys(errors).length ? errors : null;
  };
}
